import type { FC, ReactNode, SVGProps } from 'react';

/**
 * The prototype's own stroke icons, path for path, so the screens match it exactly.
 *
 * Every icon is `currentColor` unless the caller passes a `stroke`; size and stroke width default
 * to the values the prototype uses most (14 px, 2).
 */
interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number;
}

const make =
  (paths: ReactNode, defaults: { strokeWidth?: number; linejoin?: boolean } = {}): FC<IconProps> =>
  ({ size = 14, strokeWidth = defaults.strokeWidth ?? 2, ...rest }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin={defaults.linejoin ? 'round' : undefined}
      aria-hidden="true"
      {...rest}
    >
      {paths}
    </svg>
  );

export const GearIcon = make(
  <>
    <path d="M12.2 3h-.4a2 2 0 0 0-2 2v.2a2 2 0 0 1-1 1.7l-.3.2a2 2 0 0 1-2 0l-.2-.1a2 2 0 0 0-2.7.7l-.2.4a2 2 0 0 0 .7 2.7l.2.1a2 2 0 0 1 1 1.7v.4a2 2 0 0 1-1 1.7l-.2.1a2 2 0 0 0-.7 2.7l.2.4a2 2 0 0 0 2.7.7l.2-.1a2 2 0 0 1 2 0l.3.2a2 2 0 0 1 1 1.7V19a2 2 0 0 0 2 2h.4a2 2 0 0 0 2-2v-.2a2 2 0 0 1 1-1.7l.3-.2a2 2 0 0 1 2 0l.2.1a2 2 0 0 0 2.7-.7l.2-.4a2 2 0 0 0-.7-2.7l-.2-.1a2 2 0 0 1-1-1.7v-.4a2 2 0 0 1 1-1.7l.2-.1a2 2 0 0 0 .7-2.7l-.2-.4a2 2 0 0 0-2.7-.7l-.2.1a2 2 0 0 1-2 0l-.3-.2a2 2 0 0 1-1-1.7V5a2 2 0 0 0-2-2z" />
    <circle cx="12" cy="12" r="2.6" />
  </>
);

export const HomeIcon = make(
  <>
    <path d="M3 10.5L12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
    <path d="M10 21v-6h4v6" />
  </>,
  { linejoin: true }
);

export const LogoutIcon = make(
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </>
);

export const ShieldIcon = make(<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />);

export const PulseIcon = make(<path d="M3 12h4l2-5 3 10 2-5h7" />);

export const CheckIcon = make(<path d="M20 6L9 17l-5-5" />);

export const LockIcon = make(
  <>
    <rect x="4" y="11" width="16" height="10" rx="1.5" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>
);

export const InfoIcon = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </>
);

export const UploadIcon = make(
  <>
    <path d="M12 16V4" />
    <path d="M7 9l5-5 5 5" />
    <path d="M20 16v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3" />
  </>
);

export const PlayIcon = make(<path d="M5 3l14 9-14 9V3z" />);

export const DownloadIcon = make(
  <>
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M20 16v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-3" />
  </>
);

export const CopyIcon = make(
  <>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15V5a2 2 0 0 1 2-2h10" />
  </>,
  { linejoin: true }
);

export const ChevronLeftIcon = make(<path d="M15 18l-6-6 6-6" />);

export const ChevronRightIcon = make(<path d="M9 18l6-6-6-6" />);

export const MinusIcon = make(<path d="M5 12h14" />);

export const PlusIcon = make(
  <>
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </>
);

export const TrashIcon = make(
  <>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M6 6l1 14h10l1-14" />
  </>
);

export const CloseIcon = make(
  <>
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </>
);

export const WarningIcon = make(
  <>
    <path d="M12 3l10 18H2L12 3z" />
    <path d="M12 10v5" />
    <path d="M12 18h.01" />
  </>,
  { linejoin: true }
);

export const PencilIcon = make(
  <>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </>,
  { linejoin: true }
);

/** The padlock of a locked item (rounded body, as in the prototype's table). */
export const PadlockIcon = make(
  <>
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
  { linejoin: true }
);

export const SearchIcon = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </>
);

export const ChevronDownIcon = make(<path d="M6 9l6 6 6-6" />);

/** The category list's disclosure chevron. */
export const DisclosureIcon = make(<path d="M9 6l6 6-6 6" />, { linejoin: true });

export const PagePrevIcon = make(<path d="M15 6l-6 6 6 6" />);

export const PageNextIcon = make(<path d="M9 6l6 6-6 6" />);

export const FileUploadIcon = make(
  <>
    <path d="M12 15V3" />
    <path d="M7 8l5-5 5 5" />
    <path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
  </>,
  { linejoin: true }
);
